import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PortalShell from '../components/PortalShell';
import ResponsiveTable from '../components/ui/ResponsiveTable';
import { Globals } from '../globalfunctions.js';
import { apiClient } from '../utils/apiClient';
import './Uptime.css';

// Same cadence the server checks on, so a dashboard left open on a wall
// doesn't quietly go stale - matches the staff Uptime page.
const REFRESH_MS = 60000;

const formatWhen = (value) => {
  if (!value) return 'Never';
  const date = new Date(value);
  const minutes = Math.round((Date.now() - date.getTime()) / 60000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes} min ago`;
  if (minutes < 60 * 24) return `${Math.round(minutes / 60)} h ago`;
  return date.toLocaleString();
};

const formatUptime = (value) => (value === null || value === undefined ? '—' : `${value}%`);

const uptimeClass = (value) => {
  if (value === null || value === undefined) return '';
  if (value >= 99.5) return 'uptime-good';
  if (value >= 95) return 'uptime-warn';
  return 'uptime-bad';
};

function PortalUptime() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (Globals.LoginTime === null) {
      localStorage.setItem('redirectAfterLogin', window.location.pathname);
      navigate('/login');
    }
  }, [navigate]);

  const load = useCallback(async () => {
    try {
      setData(await apiClient.get('/api/uptime/mine'));
      setError('');
    } catch (err) {
      console.error('Error loading uptime:', err);
      setError(err.message || 'Could not load your outage tracker.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const timer = setInterval(load, REFRESH_MS);
    return () => clearInterval(timer);
  }, [load]);

  const columns = [
    {
      key: 'hostname',
      label: 'Site',
      sortable: true,
      render: (site) => (
        <div className="uptime-site">
          <span className={`uptime-dot uptime-dot-${site.state}`} aria-hidden="true"></span>
          <div>
            <div className="uptime-host">{site.hostname}</div>
            {site.state === 'down' && site.last_error && (
              <div className="uptime-reason">
                {site.last_error} · {site.consecutive_failures} failed check{site.consecutive_failures === 1 ? '' : 's'} in a row
              </div>
            )}
          </div>
        </div>
      )
    },
    {
      key: 'state',
      label: 'Status',
      sortable: true,
      render: (site) => <span className={`uptime-badge uptime-badge-${site.state}`}>{site.state}</span>
    },
    {
      key: 'last_response_ms',
      label: 'Response',
      sortable: true,
      sortValue: (site) => Number(site.last_response_ms) || 0,
      render: (site) => (site.state === 'up' && site.last_response_ms ? `${site.last_response_ms} ms` : '—')
    },
    {
      key: 'uptime_24h',
      label: '24h',
      sortable: true,
      sortValue: (site) => (site.uptime_24h === null ? -1 : site.uptime_24h),
      render: (site) => <span className={uptimeClass(site.uptime_24h)}>{formatUptime(site.uptime_24h)}</span>
    },
    {
      key: 'uptime_7d',
      label: '7d',
      sortable: true,
      sortValue: (site) => (site.uptime_7d === null ? -1 : site.uptime_7d),
      render: (site) => <span className={uptimeClass(site.uptime_7d)}>{formatUptime(site.uptime_7d)}</span>
    },
    {
      key: 'uptime_30d',
      label: '30d',
      sortable: true,
      sortValue: (site) => (site.uptime_30d === null ? -1 : site.uptime_30d),
      render: (site) => <span className={uptimeClass(site.uptime_30d)}>{formatUptime(site.uptime_30d)}</span>
    },
    {
      key: 'last_checked_at',
      label: 'Checked',
      sortable: true,
      render: (site) => formatWhen(site.last_checked_at)
    }
  ];

  const summary = data?.summary;

  return (
    <PortalShell>
      <div className="uptime-header">
        <div>
          <h1>Outages</h1>
          <p className="uptime-intro">Every site we invoice you for is checked every 5 minutes.</p>
        </div>
      </div>

      {error && <div className="uptime-error">{error}</div>}

      {summary && (
        <div className="uptime-summary">
          <div className="uptime-card uptime-card-up">
            <span className="uptime-card-value">{summary.up}</span>
            <span className="uptime-card-label">Up</span>
          </div>
          <div className={`uptime-card ${summary.down > 0 ? 'uptime-card-down' : ''}`}>
            <span className="uptime-card-value">{summary.down}</span>
            <span className="uptime-card-label">Down</span>
          </div>
          <div className="uptime-card">
            <span className="uptime-card-value">{summary.pending}</span>
            <span className="uptime-card-label">Not yet checked</span>
          </div>
          <div className="uptime-card">
            <span className="uptime-card-value">{summary.total}</span>
            <span className="uptime-card-label">Sites watched</span>
          </div>
        </div>
      )}

      {loading ? (
        <p>Loading your outage tracker...</p>
      ) : (
        <ResponsiveTable
          columns={columns}
          rows={data?.sites || []}
          rowKey="hostname"
          emptyMessage="No sites to watch yet — a site appears here once a hostname is put on one of your invoices."
        />
      )}
    </PortalShell>
  );
}

export default PortalUptime;
